'use client';

import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { createContext, type PropsWithChildren, type ReactNode, useEffect, useRef } from 'react';
import { ServiceUnavailableDialog, UpdatesInProgressDialog } from '@/components/common/Dialog';
import { useServiceUnavailable } from '@/contexts/ServiceUnavailable/ServiceUnavailable.context';
import { useUpdatesInProgress } from '@/contexts/UpdatesInProgress/UpdatesInProgress.context';
import { getActiveDialog } from '@/helpers/dialog';
import { useTermsOfUseAccepted } from '../../TermsOfUse/TermsOfUse.context';
import { useDialogOnMountDataContext } from '../DialogOnMountData/DialogOnMountData.context';
type DialogOnMountContextProps = Record<string, never>;
const DialogOnMountContext = createContext<DialogOnMountContextProps | null>(null);

/** @internal */
export const DialogOnMountContextProvider = ({
  children
}: PropsWithChildren) => {
  const {
    dialogs
  } = useDialogOnMountDataContext();
  const {
    toggleDialog,
    isOpen,
    dialogContent
  } = useDialogContext();
  const {
    isServiceUnavailableDialogOpen
  } = useServiceUnavailable();
  const {
    isUpdatesInProgressDialogOpen,
    closeUpdatesInProgressDialog
  } = useUpdatesInProgress();
  const termsAccepted = useTermsOfUseAccepted();

  // Ownership of the global dialog slot: the on-mount dialog this provider opened, matched
  // by content identity so a dialog opened elsewhere (even while `isOpen` stays true) is
  // never ours.
  const ownedDialogRef = useRef<{
    index: number;
    component: ReactNode;
  } | null>(null);
  const dialogContentRef = useRef(dialogContent);
  useEffect(() => {
    dialogContentRef.current = dialogContent;
  }, [dialogContent]);
  useEffect(() => {
    if (isOpen) {
      const owned = ownedDialogRef.current;
      if (!owned) return;
      if (dialogContentRef.current !== owned.component) {
        ownedDialogRef.current = null;
        return;
      }
      if (dialogs[owned.index]?.condition) return;
      ownedDialogRef.current = null;
      toggleDialog(false);
    }
    const {
      dialog
    } = getActiveDialog(dialogs);
    if (dialog) {
      ownedDialogRef.current = {
        index: dialogs.indexOf(dialog),
        component: dialog.component
      };
      toggleDialog(dialog.component, dialog.dialogOptions);
    }
    // isOpen is read inside the effect as a guard but intentionally omitted
    // from the dep array. Including it would re-run on dismissal: when
    // toggleDialog(false) flips isOpen synchronously, an async router.replace
    // (e.g. clearFromLiteParam) hasn't yet updated useSearchParams, so the
    // dialogs memo still considers the lite dialog active and re-opens it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogs, toggleDialog]);

  /**
   * This effect is responsible for opening the ServiceUnavailableDialog
   * when some critical calls fail.
   * This is the only place where we open the dialog.
   */
  useEffect(() => {
    if (termsAccepted && isServiceUnavailableDialogOpen && !isOpen) {
      toggleDialog(<ServiceUnavailableDialog />, {
        closable: false,
        maxWidth: '400px'
      });
    }
  }, [termsAccepted, isServiceUnavailableDialogOpen, toggleDialog, isOpen]);

  /**
   * Opens the UpdatesInProgressDialog when the flag is active.
   * ServiceUnavailableDialog takes precedence if both are open.
   */
  useEffect(() => {
    if (termsAccepted && isUpdatesInProgressDialogOpen && !isServiceUnavailableDialogOpen && !isOpen) {
      toggleDialog(<UpdatesInProgressDialog />, {
        closable: true,
        maxWidth: '400px',
        onClose: closeUpdatesInProgressDialog
      });
    }
  }, [termsAccepted, isUpdatesInProgressDialogOpen, isServiceUnavailableDialogOpen, toggleDialog, isOpen, closeUpdatesInProgressDialog]);
  return <DialogOnMountContext.Provider value={{}}>{children}</DialogOnMountContext.Provider>;
};
export default DialogOnMountContextProvider;